import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import logger from 'src/util/logger';

/**
 * Description
 * @param {object} props
 * @param {object} props.relay
 * @param {object} props.viewer
 * @returns {React.ReactElement} - AdminPropertyList component
 */
function AdminPropertyUserList({ relay, viewer = null }) {
  const loadMore = () => {
    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  const intl = useIntl();

  if (!viewer) {
    return <Loading />;
  }

  const { propertyUsers } = viewer;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.page.title', defaultMessage: 'Enosi Admin - Property Users' },
  );
  const breadcrumbAdmin = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbPropertyUsers = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.breadcrumbs.property_users.label', defaultMessage: 'Property Users' },
  );

  // Table headers
  const tableStatusHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.status.header', defaultMessage: 'Status' },
  );
  const tableIdHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.id.header', defaultMessage: 'ID' },
  );
  const tableExternalIdentifierTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.external_identifier.title', defaultMessage: 'External identifier' },
  );
  const tableExternalIdentifierLabel = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.external_identifier.label', defaultMessage: 'Ext ID' },
  );
  const tableUserHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.user.header', defaultMessage: 'User' },
  );
  const tablePropertyHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.table.property.header', defaultMessage: 'Property' },
  );

  // Action buttons
  const addPropertyUserButton = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.action.add_property_user.label', defaultMessage: 'Add Property User' },
  );

  // Load more button text
  const copyLoadMore = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' },
  );
  const copyNoMore = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_list.load_more_button.state_no_more.label', defaultMessage: 'No more property users to load' },
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPropertyUsers },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbPropertyUsers}</h1>
        <div className="float-end mb-4">
          <ButtonToolbar>
            <ButtonGroup className="ms-2">
              <Link
                to="/admin/property-users/new"
                className="btn btn-darken"
              >
                {addPropertyUserButton}
              </Link>
            </ButtonGroup>
          </ButtonToolbar>
        </div>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{tableStatusHeader}</th>
                <th scope="col">
                  <abbr title={tableIdHeader}>ID</abbr>
                </th>
                <th scope="col">
                  <abbr title={tableExternalIdentifierTitle}>{tableExternalIdentifierLabel}</abbr>
                </th>
                <th scope="col">{tableUserHeader}</th>
                <th scope="col">{tablePropertyHeader}</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                propertyUsers && propertyUsers.edges && propertyUsers.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    active, uuid, externalIdentifier, user, property,
                  } = node;
                  const {
                    uuid: userId, active: userActive, email: userEmail,
                  } = user;
                  const {
                    uuid: propertyId, title: propertyTitle, active: propertyActive,
                  } = property;

                  return (
                    <tr key={`admin-property-user-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={2}>
                        <UUID uuid={uuid} url={`/admin/property-users/${uuid}`} />
                        <br />
                        {`(${externalIdentifier})`}
                      </th>
                      <td>
                        <Link to={`/admin/users/${userId}`}>
                          {displayUserName(intl, user)}
                        </Link>
                        <br />
                        {userEmail}
                        <br />
                        <BadgeActive
                          start={userActive.start}
                          finish={userActive.finish}
                        />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${propertyId}`}>
                          {propertyTitle}
                        </Link>
                        <br />
                        <UUID uuid={propertyId} />
                        <br />
                        <BadgeActive
                          start={propertyActive.start}
                          finish={propertyActive.finish}
                        />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            hasNext={relay.hasMore()}
            onClick={loadMore}
          />
        </div>
      </div>
    </>
  );
}

AdminPropertyUserList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
    viewerUser: PropTypes.shape({
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
    }),
    propertyUsers: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            uuid: PropTypes.string,
            externalIdentifier: PropTypes.string,
            active: PropTypes.shape({
              start: PropTypes.number,
              finish: PropTypes.number,
            }),
            property: PropTypes.shape({
              uuid: PropTypes.string,
              title: PropTypes.string,
              active: PropTypes.shape({
                start: PropTypes.number,
                finish: PropTypes.number,
              }),
              address: PropTypes.shape({
                line1: PropTypes.string,
                line2: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                postcode: PropTypes.string,
              }),
            }),
            user: PropTypes.shape({
              uuid: PropTypes.string,
              email: PropTypes.string,
              givenName: PropTypes.string,
              familyName: PropTypes.string,
              active: PropTypes.shape({
                start: PropTypes.number,
                finish: PropTypes.number,
              }),
            }),
          }),
        }),
      ),
    }),
  }),
};

export default createPaginationContainer(
  AdminPropertyUserList,
  {
    viewer: graphql`
      fragment AdminPropertyUserList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        propertyUsers(
          first: $count
          after: $cursor
        ) @connection(key: "AdminPropertyUserList_propertyUsers") {
          edges {
            cursor
            node {
              uuid
              externalIdentifier
              active {
                start
                finish
              }
              property {
                uuid
                title
                active { start finish }
              }
              user {
                uuid
                email
                givenName
                familyName
                active { start finish }
              }
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.propertyUsers;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminPropertyUserList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminPropertyUserList_viewer
      }
    }
  `,
  },
);
